import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import GoogleIcon from "@mui/icons-material/Google";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { loginService } from "../services/loginService";
import { COLORS } from "../theme/theme-options";

type NewPasswordChallenge = {
  challengeName: "NEW_PASSWORD_REQUIRED";
  username: string;
  session: string;
  requiredAttributes?: string[];
};

type NoticeState = {
  severity: "info" | "success";
  message: string;
};

const isNewPasswordChallenge = (value: unknown): value is NewPasswordChallenge => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    candidate.challengeName === "NEW_PASSWORD_REQUIRED" &&
    typeof candidate.username === "string" &&
    typeof candidate.session === "string"
  );
};

const isCognitoServiceError = (value: unknown): value is { message: string } => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  const message = candidate.message;
  if (typeof message !== "string" || !message.trim()) {
    return false;
  }

  const hasExceptionName =
    typeof candidate.name === "string" && candidate.name.endsWith("Exception");
  const hasMetadata =
    typeof candidate.$metadata === "object" && candidate.$metadata !== null;
  const hasType = typeof candidate.__type === "string";

  return hasExceptionName || hasMetadata || hasType;
};

const resolveLoginErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (isCognitoServiceError(error)) {
    return error.message;
  }

  console.error("Login flow failed", error);
  return fallbackMessage;
};

const compliesWithPasswordPolicy = (password: string): boolean => {
  if (password.length < 8) {
    return false;
  }

  return (
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /\d/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recoveryCode = searchParams.get("code")?.trim() ?? "";
  const passwordResetDone = searchParams.get("password-reset") === "success";
  const recoverUsernameHint = searchParams.get("user")?.trim() ?? "";
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<NoticeState | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmNewPassword, setConfirmNewPassword] = React.useState("");
  const [challenge, setChallenge] = React.useState<NewPasswordChallenge | null>(
    null,
  );

  const isForceChangePasswordFlow = Boolean(challenge);
  const isRecoverFallbackFlow = recoveryCode.length > 0;
  const isPasswordResetFlow =
    isForceChangePasswordFlow || isRecoverFallbackFlow;

  React.useEffect(() => {
    if (!passwordResetDone) {
      return;
    }

    setNotice({
      severity: "success",
      message: "Password reset completed. You can now sign in.",
    });
    setError(null);
    setPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  }, [passwordResetDone]);

  React.useEffect(() => {
    if (!isRecoverFallbackFlow) {
      return;
    }

    if (!email.trim() && recoverUsernameHint) {
      setEmail(recoverUsernameHint);
    }
  }, [email, isRecoverFallbackFlow, recoverUsernameHint]);

  React.useEffect(() => {
    const username = searchParams.get("user")?.trim();
    const temporaryPassword = searchParams.get("key")?.trim();

    if (!username || !temporaryPassword) {
      return;
    }

    let isCancelled = false;

    setEmail(username);
    setPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setChallenge(null);
    setNotice(null);

    const startForceChangePasswordFlow = async () => {
      try {
        setLoading(true);
        setError(null);
        setNotice(null);

        const response = await loginService.redeemUsernamePassword({
          username,
          password: temporaryPassword,
        });

        if (isCancelled) {
          return;
        }

        if (isNewPasswordChallenge(response)) {
          setChallenge(response);
          setEmail(response.username);
          return;
        }

        navigate("/dashboards", { replace: true });
      } catch (bootstrapError) {
        if (isCancelled) {
          return;
        }

        setError(
          resolveLoginErrorMessage(
            bootstrapError,
            "Unable to start force-change-password flow. Please sign in manually.",
          ),
        );
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    void startForceChangePasswordFlow();

    return () => {
      isCancelled = true;
    };
  }, [navigate, searchParams]);

  const setLoginError = (error: unknown, fallbackMessage: string) => {
    setNotice(null);
    setError(resolveLoginErrorMessage(error, fallbackMessage));
  };

  const onStartForgotPassword = async () => {
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setNotice(null);

      const response = await loginService.beginForgotPassword({
        username: email,
      });

      setEmail(response.username);
      setPassword("");
      setNewPassword("");
      setConfirmNewPassword("");

      const deliveryMessage = response.destination
        ? `Email sent to ${response.destination}. Open the link you received to complete the password reset.`
        : "Email sent. Open the link you received to complete the password reset.";

      setNotice({
        severity: "info",
        message: deliveryMessage,
      });
    } catch (forgotPasswordError) {
      setLoginError(
        forgotPasswordError,
        "Unable to start password reset. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const onBackToSignIn = () => {
    setChallenge(null);
    setNewPassword("");
    setConfirmNewPassword("");
    setPassword("");
    setError(null);

    if (isRecoverFallbackFlow) {
      navigate("/login", { replace: true });
      return;
    }

    setNotice(null);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (challenge) {
      if (!newPassword.trim() || !confirmNewPassword.trim()) {
        setError("New password and confirmation are required.");
        return;
      }

      if (newPassword !== confirmNewPassword) {
        setError("Passwords do not match.");
        return;
      }

      if (newPassword.length < 8) {
        setError("New password must be at least 8 characters.");
        return;
      }

      if (!compliesWithPasswordPolicy(newPassword)) {
        setError(
          "New password must include at least 8 characters, 1 lowercase letter, 1 uppercase letter, 1 digit, and 1 special character.",
        );
        return;
      }

      try {
        setLoading(true);
        await loginService.completeNewPasswordChallenge({
          username: challenge.username,
          session: challenge.session,
          newPassword,
        });
        navigate("/dashboards", { replace: true });
      } catch (submitError) {
        setLoginError(
          submitError,
          "An unexpected error occurred. Please try again.",
        );
      } finally {
        setLoading(false);
      }

      return;
    }

    if (isRecoverFallbackFlow) {
      if (!email.trim()) {
        setError("Email is required.");
        return;
      }
      if (!newPassword.trim() || !confirmNewPassword.trim()) {
        setError("New password and confirmation are required.");
        return;
      }

      if (newPassword !== confirmNewPassword) {
        setError("Passwords do not match.");
        return;
      }

      if (!compliesWithPasswordPolicy(newPassword)) {
        setError(
          "New password must include at least 8 characters, 1 lowercase letter, 1 uppercase letter, 1 digit, and 1 special character.",
        );
        return;
      }

      try {
        setLoading(true);
        await loginService.confirmForgotPassword({
          username: email,
          confirmationCode: recoveryCode,
          newPassword,
        });
        navigate("/login?password-reset=success", { replace: true });
      } catch (forgotPasswordError) {
        setLoginError(
          forgotPasswordError,
          "Unable to complete password reset. Please try again.",
        );
      } finally {
        setLoading(false);
      }

      return;
    }

    if (!email.trim() || !password.trim()) {
      setError("Email and password are required.");
      return;
    }

    try {
      setLoading(true);
      const response = await loginService.redeemUsernamePassword({
        username: email,
        password,
      });

      if (isNewPasswordChallenge(response)) {
        setChallenge(response);
        setEmail(response.username);
        setPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        return;
      }

      navigate("/dashboards", { replace: true });
    } catch (submitError) {
      setLoginError(
        submitError,
        "An unexpected error occurred. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLogin = () => {
    setError(null);
    setNotice(null);
    const callbackUrl = loginService.beginGoogleOAuth();
    navigate(callbackUrl);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 4 },
        background:
          "radial-gradient(circle at 8% 8%, rgba(34,211,238,0.16) 0%, rgba(6,13,26,1) 42%), #060d1a",
        display: "grid",
        placeItems: "center",
      }}
    >
      <Card
        variant="glass"
        sx={{
          width: "100%",
          maxWidth: 1020,
          border: `1px solid ${alpha(COLORS.grey[700], 0.75)}`,
          boxShadow: "0 28px 70px rgba(2, 6, 23, 0.5)",
          overflow: "hidden",
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
            minHeight: { xs: "auto", md: 620 },
          }}
        >
          <Stack
            component="form"
            spacing={2}
            onSubmit={onSubmit}
            sx={{
              px: { xs: 2.5, sm: 3.5, md: 5 },
              py: { xs: 3, md: 5 },
              justifyContent: "center",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  color: COLORS.primary[300],
                  bgcolor: alpha(COLORS.primary[500], 0.18),
                  border: `1px solid ${alpha(COLORS.primary[400], 0.32)}`,
                }}
              >
                <BoltRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ color: COLORS.grey[100], fontWeight: 800, fontSize: 31 }}>
                Pure IT
              </Typography>
            </Stack>

            <Typography sx={{ color: COLORS.grey[500], mb: 1.6 }}>
              {isForceChangePasswordFlow
                ? "Set a new password to continue"
                : isRecoverFallbackFlow
                  ? "Recover your account"
                  : "Sign in to continue"}
            </Typography>

            {isPasswordResetFlow && (
              <Alert severity="warning">
                Password policy: minimum 8 characters, at least 1 lowercase
                letter, 1 uppercase letter, 1 digit, and 1 special character.
              </Alert>
            )}

            <FormControl>
              <FormLabel>Email</FormLabel>
              <TextField
                size="small"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.com"
                disabled={isForceChangePasswordFlow}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineRoundedIcon sx={{ fontSize: 18, color: COLORS.grey[500] }} />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            {!isPasswordResetFlow ? (
              <FormControl>
                <FormLabel>Password</FormLabel>
                <TextField
                  size="small"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ fontSize: 18, color: COLORS.grey[500] }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            ) : (
              <>
                <FormControl>
                  <FormLabel>New Password</FormLabel>
                  <TextField
                    size="small"
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="At least 8 characters"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon
                            sx={{ fontSize: 18, color: COLORS.grey[500] }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Confirm New Password</FormLabel>
                  <TextField
                    size="small"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(event) => setConfirmNewPassword(event.target.value)}
                    placeholder="Repeat new password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon
                            sx={{ fontSize: 18, color: COLORS.grey[500] }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </>
            )}

            {!isPasswordResetFlow && (
              <Button
                variant="text"
                disabled={loading}
                onClick={onStartForgotPassword}
                sx={{ width: "fit-content", px: 0, minWidth: 0 }}
              >
                Forgot password?
              </Button>
            )}

            {notice && <Alert severity={notice.severity}>{notice.message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              startIcon={<LoginRoundedIcon fontSize="small" />}
              disabled={loading}
              sx={{ mt: 0.5, width: { xs: "100%", sm: 190 } }}
            >
              {loading
                ? isForceChangePasswordFlow
                  ? "Updating..."
                  : isRecoverFallbackFlow
                    ? "Resetting..."
                    : "Logging in..."
                : isForceChangePasswordFlow
                  ? "Update password"
                  : isRecoverFallbackFlow
                    ? "Reset password"
                    : "Login"}
            </Button>

            {isPasswordResetFlow && (
              <Button
                variant="text"
                disabled={loading}
                onClick={onBackToSignIn}
                sx={{ width: { xs: "100%", sm: 190 } }}
              >
                Back to sign in
              </Button>
            )}

            {!isPasswordResetFlow && (
              <>
                <Divider
                  sx={{
                    color: COLORS.grey[600],
                    fontSize: 12,
                    "&::before, &::after": {
                      borderColor: alpha(COLORS.grey[700], 0.7),
                    },
                  }}
                >
                  or
                </Divider>

                <Button
                  variant="outlined"
                  startIcon={<GoogleIcon fontSize="small" />}
                  onClick={onGoogleLogin}
                  disabled={loading}
                  sx={{ width: { xs: "100%", sm: 190 } }}
                >
                  Login with Google
                </Button>
              </>
            )}
          </Stack>

          <Box
            sx={{
              position: "relative",
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              px: 5,
              overflow: "hidden",
              borderLeft: `1px solid ${alpha(COLORS.grey[700], 0.5)}`,
              background:
                "linear-gradient(145deg, rgba(22,78,99,0.16) 0%, rgba(8,47,73,0.08) 45%, rgba(3,7,18,0.2) 100%)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -160,
                right: -160,
                width: 360,
                height: 360,
                borderRadius: "50%",
                border: `36px solid ${alpha(COLORS.primary[500], 0.16)}`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 42,
                left: 46,
                width: 62,
                height: 62,
                borderRadius: "50%",
                backgroundColor: alpha(COLORS.primary[400], 0.26),
                filter: "blur(1px)",
              }}
            />
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                color: COLORS.primary[200],
                bgcolor: alpha(COLORS.primary[500], 0.18),
                border: `1px solid ${alpha(COLORS.primary[400], 0.38)}`,
                mb: 3,
              }}
            >
              <BoltRoundedIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography
              sx={{
                color: COLORS.grey[100],
                fontWeight: 800,
                fontSize: 32,
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              Welcome back
            </Typography>
            <Typography
              sx={{
                mt: 1.4,
                color: COLORS.grey[400],
                textAlign: "center",
                fontSize: 19,
                maxWidth: 360,
              }}
            >
              You are one step away from your operational dashboard.
            </Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default LoginPage;
